import type { ObjectMetadata } from '@olytic/foundation';

import { formatMoney, formatNumber } from '../../objects/format';
import { OBJECT_DEFS } from '../../objects/objectDefs';
import {
  activityColumns, asDate, asLabel, column, createdByField, documentColumns, lastModifiedByField, lookup, meetingColumns,
  membershipColumns, listColumnsOf, related, type Row, schemaRecord, show, text,
} from './kit';

const def = OBJECT_DEFS.accounts;

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type; any column not placed below appears in Other Fields. Josh,
 * 2026-10-19: funding stage, the AI-readiness checkboxes and aliases left the
 * page; their columns remain in lists, filters and reports.
 */
const field = schemaRecord('accounts', {
  hidden: ['funding_stage', 'has_ai_tooling', 'has_ai_headcount', 'has_revops_hire', 'aliases'],
});

const details = [
  field('name', 'Account Name'),
  field('status', 'Status', { help: "Prospect until they buy, Active while they're a customer, Former after." }),
  field('domain', 'Domain'),
  field('industry', 'Industry'),
  // A state code and a band's name read as they are, not as codes to humanize.
  field('hq_state', 'HQ State', { format: text }),
  field('icp_score', 'ICP Score', { help: 'How closely the account matches our ideal customer, from 0 to 100.' }),
  field('primary_contact_name', 'Primary Contact'),
  field('primary_contact_email', 'Primary Contact Email'),
  field('employee_count', 'Employees'),
  field('annual_revenue', 'Annual Revenue'),
  field('company_size', 'Company Size', { format: text, help: 'Worked out from Employees and Annual Revenue, so it changes when they do.' }),
  field('notes', 'Notes', { wide: true }),
  field('created_at', 'Created'),
  field('updated_at', 'Last Modified'),
  field('apollo_org_id', 'Apollo Organization ID'),
  field('apollo_enriched_at', 'Enriched From Apollo'),
  createdByField(),
  lastModifiedByField(),
];

/** An account: a company Olytic sells to or works with. Rationale: accounts.md. */
export const accounts: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Account',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['name', 'status', 'domain', 'industry', 'hq_state', 'icp_score', 'primary_contact_name', 'primary_contact_email'] },
    { key: 'profile', label: 'Company Profile', fields: ['employee_count', 'annual_revenue', 'company_size'] },
    { key: 'notes', label: 'Notes', fields: ['notes'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name', 'apollo_org_id', 'apollo_enriched_at'] },
  ],
  related: [
    related('contacts', 'Contacts', 'contacts', [
      column('name', 'Name', { primary: true }),
      column('title', 'Title'),
      column('email', 'Email'),
      column('is_primary_contact', 'Primary'),
    ]),
    related('opportunities', 'Opportunities', 'opportunities', [
      column('name', 'Opportunity', { primary: true }),
      column('stage', 'Stage', { format: asLabel }),
      column('amount', 'Amount', { format: show(formatMoney), align: 'right' }),
      column('close_date', 'Close Date', { format: asDate }),
      column('owner_name', 'Owner'),
    ]),
    related('audiences', 'Audiences', 'audiences', membershipColumns()),
    related('quotes', 'Quotes', 'quotes', [
      column('quote_number', 'Quote', { primary: true }),
      column('name', 'Name'),
      column('status', 'Status', { format: asLabel }),
      column('one_time_net', 'One-Time', { format: show(formatMoney), align: 'right' }),
      column('monthly_net', 'Monthly', { format: show(formatMoney), align: 'right' }),
    ]),
    related('assets', 'Assets', 'assets', [
      column('name', 'Asset', { primary: true }),
      column('billing', 'Billing', { format: asLabel }),
      column('unit_price', 'Price', { format: show(formatMoney), align: 'right' }),
      column('start_date', 'Start', { format: asDate }),
    ]),
    related('projects', 'Projects', 'projects', [
      column('name', 'Project', { primary: true }),
      column('code', 'Code'),
      column('owner_name', 'Owner'),
      column('created_at', 'Created', { format: asDate }),
    ]),
    related('time-entries', 'Time Entries', 'time-entries', [
      column('entry_date', 'Date', { primary: true, format: asDate }),
      column('project_name', 'Project', { link: lookup('projects', 'project_id') }),
      column('hours', 'Hours', { format: show((v) => formatNumber(v)), align: 'right' }),
      column('billable', 'Billable'),
      column('owner_name', 'Person'),
    ]),
    related('billing-accounts', 'Billing Accounts', 'billing-accounts', [
      column('name', 'Billing Account', { primary: true }),
      column('is_default', 'Default'),
      column('payment_terms', 'Terms', { format: asLabel }),
      column('invoice_email', 'Invoice Email'),
    ]),
    related('invoices', 'Invoices', 'invoices', [
      column('name', 'Invoice', { primary: true }),
      column('invoice_date', 'Date', { format: asDate }),
      column('status', 'Status', { format: asLabel }),
      column('total', 'Total', { format: show(formatMoney), align: 'right' }),
    ]),
    related('contracts', 'Contracts', 'contracts', [
      column('name', 'Contract', { primary: true }),
      column('start_date', 'Start', { format: asDate }),
      column('end_date', 'End', { format: asDate }),
      column('status', 'Status', { format: asLabel }),
    ]),
    related('meetings', 'Meetings', 'meetings', meetingColumns()),
    related('activities', 'Activities', 'activities', activityColumns()),
    related('documents', 'Documents', 'documents', documentColumns()),
  ],
  tabs: [
    { key: 'information', label: 'Information', details: true },
    { key: 'sales', label: 'Sales', related: ['contacts', 'opportunities', 'quotes', 'audiences'] },
    { key: 'delivery', label: 'Delivery', related: ['projects', 'assets', 'time-entries'] },
    // History, not Activity (MOTHER-224): the Activity tab is the timeline ObjectRecordView adds after Information.
    { key: 'history', label: 'History', related: ['meetings', 'activities', 'documents'] },
    { key: 'billing', label: 'Billing', related: ['invoices', 'contracts', 'billing-accounts'] },
  ],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'domain', 'industry'],
};
